import React from "react";
import {CaptionStepComponent, FullCaptionComponent} from "../../../../../app/components/caption/CaptionComponent";
import {CodeComponent} from "../../../../../app/components/code/CodeComponent";
import LinkComponent, {ALinkTarget} from "../../../../../app/components/link/LinkComponent";
import {Paths} from "../../../../../app/components/sidebar/Paths";

export function HashSetRemoveNeg96FullCaption() {
    return (<FullCaptionComponent title="VALUE: -96">
        <HashSetRemoveNeg961/>
        <HashSetRemoveNeg962/>
        <HashSetRemoveNeg963/>
        <HashSetRemoveNeg964/>
    </FullCaptionComponent>)
}

export function HashSetRemoveNeg961() {
    const generateHashFunction = "fun generateHash(value: Int): Int {return value % arraySize}"
    return (<CaptionStepComponent title={<h6>1. Generate <CodeComponent
        className="codeHash">HASH</CodeComponent>. <CodeComponent>O(1)</CodeComponent></h6>}>
        <p>Unlike inserting, we typically do <i>not</i> call a <CodeComponent>contains</CodeComponent> function. As you
            will see later on, while <CodeComponent>contains</CodeComponent> typically runs
            in <CodeComponent>O(1)</CodeComponent> time, it <i>can</i> be up to <CodeComponent>O(n)</CodeComponent> time
            in our worst case scenario. With inserting, we don't need to iterate over our sub-collection, as we are just
            adding a <CodeComponent className="codeValue">VALUE</CodeComponent> to the end.</p>
        <p>However, when <i>removing</i> a <CodeComponent className="codeValue">VALUE</CodeComponent>, we will always
            need to iterate over our sub-collection in order to find the exact <CodeComponent
                className="codeValue">VALUE</CodeComponent> we are looking to remove. Because of this distinction, we
            typically do not call the <CodeComponent>contains</CodeComponent> function when removing a <CodeComponent
                className="codeValue">VALUE</CodeComponent>. While it wouldn't asymptotically change the time complexity
            - <CodeComponent>O(2n)</CodeComponent> still reduces to <CodeComponent>O(n)</CodeComponent> - it would still
            be more time, so we might as well just make it <CodeComponent>O(n)</CodeComponent> to begin with.</p>
        <p>Therefore, in our example, assuming our hash function
            is <CodeComponent>{generateHashFunction}</CodeComponent>, if we input -96, we output a <CodeComponent
                className="codeHash">HASH</CodeComponent> of 0.</p>
    </CaptionStepComponent>)
}

export function HashSetRemoveNeg962() {
    return (<CaptionStepComponent title={<h6>2. Find sub-collection at the <CodeComponent
        className="codeHash">HASH</CodeComponent> index. <CodeComponent>O(1)</CodeComponent></h6>}>
        <p>Because our <CodeComponent className="codeHash">HASH</CodeComponent> is 0, we now want to find the
            sub-collection at index 0 of our array.</p>
    </CaptionStepComponent>)
}

export function HashSetRemoveNeg963() {
    return (<CaptionStepComponent title={<h6>3. Iterate over our sub-collection until we found the <CodeComponent
        className="codeValue">VALUE</CodeComponent> we want to
        remove. <CodeComponent><s>O(n)</s></CodeComponent><CodeComponent>O(1)</CodeComponent></h6>}>
        <p>At <CodeComponent className="codeHash">HASH</CodeComponent> index 0, we only have a single item -
            the <CodeComponent className="codeValue">VALUE</CodeComponent> we are looking for. While this
            step <i>could</i> be <CodeComponent>O(n)</CodeComponent> time, in this average case, we see the time
            complexity is really only <CodeComponent>O(1)</CodeComponent>.</p>
    </CaptionStepComponent>)
}

export function HashSetRemoveNeg964() {
    return (<CaptionStepComponent
        title={<h6>4. Remove <CodeComponent className="codeValue">VALUE</CodeComponent> from our
            sub-collection. <CodeComponent>O(1)</CodeComponent>, could also potentially
            be <CodeComponent>O(n)</CodeComponent></h6>}>
        <p>Similarly to <CodeComponent>generateHash</CodeComponent>, we typically do not want to add time complexity to
            this step. In our example, we are using arrays. Which, as we've covered in our <LinkComponent
                link={Paths.dataStructures.nested.array.path} target={ALinkTarget.internal}>Array</LinkComponent> study
            guide, we can see removing an element in a dynamically resizing array can
            be <CodeComponent>O(n)</CodeComponent> time. We could set the value to null in our Array instead in order to
            prevent extra time here, or we could use a LinkedList solution that would allow us to remove a node
            in <CodeComponent>O(1)</CodeComponent> time. It's worth noting, even if we choose to use a dynamically
            resizing array, our time complexity will still be <CodeComponent>O(2n)</CodeComponent> total to remove
            the <CodeComponent className="codeValue">VALUE</CodeComponent> (or <CodeComponent>O(3n)</CodeComponent> if
            all of our <CodeComponent className="codeValue">VALUES</CodeComponent> are located in the same
            sub-collection), which would all be reduced to the same <CodeComponent>O(n)</CodeComponent> time complexity
            asymptotically. Because there are ways to guarantee this step is <CodeComponent>O(1)</CodeComponent> time
            complexity, <CodeComponent>O(1)</CodeComponent> will be used moving forward.</p>
        <p>We remove our <CodeComponent className="codeValue">VALUE</CodeComponent> of -96 from the sub-collection.</p>
    </CaptionStepComponent>)
}
